// ────────────────────────────────────────────────────────────────────────
// GroupToolbar — 选中单个群组节点时显示的浮窗工具栏
// ────────────────────────────────────────────────────────────────────────
// 触发条件：恰好选中 1 个 type='group' 节点。
// 关键点：拖动群组时位置每帧都在变，工具栏需要跟随。所以位置不能用
// selectedNodes 这个 React state（拖拽中不更新），改为通过 useStore 订阅
// nodeInternals 实时读取最新 position。
// 提供动作：解散群组 → onUngroup(groupId)

import React from 'react';
import { useViewport, useStore } from 'reactflow';
import { Unlink, Box } from 'lucide-react';

const GroupToolbar = ({ selectedNodes, onUngroup }) => {
  const viewport = useViewport();

  // 仅当恰好选中 1 个 group 节点时显示
  const groupId =
    selectedNodes && selectedNodes.length === 1 && selectedNodes[0].type === 'group'
      ? selectedNodes[0].id
      : null;

  // 从 store 实时读取该 group 节点（拖动中位置每帧更新），避免工具栏滞后
  const liveGroup = useStore((s) => (groupId ? s.nodeInternals.get(groupId) : null));

  if (!groupId || !liveGroup) return null;

  const childCount = (liveGroup.data?.childIds || []).length;

  // flow → 屏幕坐标
  const { x: vpX, y: vpY, zoom } = viewport;
  const gx = liveGroup.position.x;
  const gy = liveGroup.position.y;
  const gw = liveGroup.width || liveGroup.style?.width || 200;
  const screenX = gx * zoom + vpX;
  const screenY = gy * zoom + vpY;
  const screenW = gw * zoom;

  const toolbarStyle = {
    position: 'absolute',
    left: screenX + screenW / 2,
    top: screenY - 50,
    transform: 'translateX(-50%)',
    zIndex: 1000,
    background: 'var(--bg-panel)',
    border: '1px solid var(--border-strong)',
    borderRadius: '8px',
    padding: '4px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
    pointerEvents: 'all',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  };

  const infoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  const buttonStyle = {
    ...infoStyle,
    color: 'var(--text-body)',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.1s',
  };

  const hoverIn = (e) => {
    e.currentTarget.style.color = 'var(--text-hover)';
    e.currentTarget.style.background = 'var(--bg-hover)';
  };
  const hoverOut = (e) => {
    e.currentTarget.style.color = 'var(--text-body)';
    e.currentTarget.style.background = 'transparent';
  };

  return (
    // nodrag/nopan：阻止 ReactFlow 把工具栏内的鼠标事件当作画布拖动/平移
    <div className="nodrag nopan" style={toolbarStyle}>
      {/* 信息：群组内节点数 */}
      <div style={infoStyle}>
        <Box size={14} />
        <span>{childCount} 个节点</span>
      </div>

      {/* 动作：解散群组 */}
      <div
        style={buttonStyle}
        onClick={() => onUngroup(groupId)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
      >
        <Unlink size={14} />
        <span>解散群组</span>
      </div>
    </div>
  );
};

export default GroupToolbar;
